import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Globe } from "lucide-react";

/**
 * Public Privacy Policy page.
 *
 *  - Source of truth for the legal privacy text. Content mirrors the DOCX
 *    supplied by the business owner (Effective 01-Jul-2026, Updated 10-Jul-2026).
 *  - Sections are numbered `<ol>` for stable anchors so future emails can
 *    deep-link (e.g. `/privacy-policy#cookies`).
 *  - If any section is added / removed / re-worded, bump `PRIVACY_VERSION`
 *    so we can prove which text was live at any given time in disputes.
 */
export const PRIVACY_VERSION = "v1";
export const PRIVACY_EFFECTIVE_DATE = "01 July 2026";
export const PRIVACY_LAST_UPDATED = "10 July 2026";

/**
 * Content model:
 *  - `body`     — single paragraph
 *  - `intro`    — lead paragraph before bullets
 *  - `groups`   — one or more titled bullet groups (used in section 2)
 *  - `bullets`  — flat bullet list
 *  - `outro`    — trailing paragraph after bullets
 *  - `contact`  — special-case renderer at the bottom
 */
export const PRIVACY_SECTIONS = [
  {
    id: "about",
    title: "About PuneSpaGuide",
    body:
      "PuneSpaGuide.com is an online directory and discovery platform that helps users find and connect with professional spa and wellness centers in Pune and Pimpri-Chinchwad (PCMC). We also allow spa owners to register, manage their business profiles, and subscribe to premium listing plans.",
  },
  {
    id: "information-we-collect",
    title: "Information We Collect",
    intro: "When you use our website, we may collect:",
    groups: [
      {
        heading: "A. Information You Provide — For Visitors",
        bullets: [
          "Name",
          "Email address",
          "Mobile number",
          "Inquiry messages",
          "Feedback or reviews submitted through our platform",
        ],
      },
      {
        heading: "A. Information You Provide — For Spa Owners",
        bullets: [
          "Owner Name",
          "Spa Name",
          "Business Address",
          "Contact Numbers",
          "Email Address",
          "GST Number (if applicable)",
          "Business Registration Details (if applicable)",
          "Business Description",
          "Images, Logos, Videos",
          "Social Media Links",
          "Business Hours",
          "Services Offered",
          "Pricing Information (optional)",
          "Bank or GST details (where required for invoicing)",
        ],
      },
      {
        heading: "B. Automatically Collected Information",
        bullets: [
          "IP Address",
          "Browser Type",
          "Device Information",
          "Operating System",
          "Pages Visited",
          "Time Spent on Website",
          "Referral Source",
          "Cookies",
          "Analytics Data",
        ],
        outro: "This information helps improve website performance and user experience.",
      },
    ],
  },
  {
    id: "how-we-use",
    title: "How We Use Your Information",
    intro: "We use your information to:",
    bullets: [
      "Create and manage user accounts",
      "Display spa listings",
      "Process subscription payments",
      "Respond to inquiries",
      "Send account-related notifications",
      "Verify spa ownership",
      "Improve website functionality",
      "Provide customer support",
      "Prevent fraud and misuse",
      "Analyze website traffic",
      "Send important service updates",
      "Send promotional communications (only where permitted)",
    ],
  },
  {
    id: "payments",
    title: "Payments",
    body:
      "Subscription payments are processed securely through trusted third-party payment gateways such as Razorpay. PuneSpaGuide does not store your debit card, credit card, UPI PIN, net banking credentials, or payment passwords. Payment information is handled securely by the payment gateway according to applicable security standards.",
  },
  {
    id: "email-communications",
    title: "Email Communications",
    intro: "We may send emails regarding:",
    bullets: [
      "Account Verification",
      "Password Reset",
      "Subscription Confirmation",
      "Payment Confirmation",
      "Renewal Reminders",
      "Plan Expiry Notifications",
      "Inquiry Notifications",
      "Important Platform Updates",
    ],
    outro: "Promotional emails may include an unsubscribe option where applicable.",
  },
  {
    id: "reviews-user-content",
    title: "Reviews & User Content",
    intro: "Users may submit:",
    bullets: ["Ratings", "Reviews", "Feedback"],
    outro:
      "By submitting content, you grant PuneSpaGuide a non-exclusive, royalty-free license to display, publish, and use such content on our platform. We reserve the right to remove reviews that are false, offensive, defamatory, misleading, spam, promotional, or illegal.",
  },
  {
    id: "cookies",
    title: "Cookies",
    intro: "We use cookies to:",
    bullets: [
      "Remember user preferences",
      "Improve website performance",
      "Analyze visitor traffic",
      "Maintain login sessions",
      "Measure advertising performance",
    ],
    outro:
      "Users can disable cookies through their browser settings, although some features may not function properly.",
  },
  {
    id: "third-party-services",
    title: "Third-Party Services",
    intro: "We may use trusted third-party services including:",
    bullets: [
      "Razorpay (Payment Processing)",
      "Google Analytics",
      "Google Maps",
      "Google reCAPTCHA",
      "Resend (Email Delivery)",
      "Cloudflare (Security & Performance)",
      "MongoDB Atlas (Database Hosting, if applicable)",
    ],
    outro: "These providers process information according to their own privacy policies.",
  },
  {
    id: "data-security",
    title: "Data Security",
    intro: "We implement commercially reasonable security measures including:",
    bullets: [
      "SSL Encryption",
      "Secure Password Storage",
      "Access Controls",
      "Database Security",
      "Firewall Protection",
      "Regular Software Updates",
    ],
    outro:
      "While we strive to protect your information, no internet transmission or storage system is completely secure.",
  },
  {
    id: "data-retention",
    title: "Data Retention",
    intro: "We retain personal information only for as long as necessary to:",
    bullets: [
      "Provide our services",
      "Comply with legal obligations",
      "Resolve disputes",
      "Enforce our agreements",
    ],
    outro: "Inactive accounts may be deleted after a reasonable period.",
  },
  {
    id: "sharing-of-information",
    title: "Sharing of Information",
    intro: "We do not sell your personal information. We may share information with:",
    bullets: [
      "Payment processors",
      "Hosting providers",
      "Email service providers",
      "Analytics providers",
      "Government authorities when legally required",
    ],
  },
  {
    id: "your-rights",
    title: "Your Rights",
    intro: "Depending on applicable law, you may request to:",
    bullets: [
      "Access your personal information",
      "Correct inaccurate information",
      "Delete your account",
      "Withdraw consent (where applicable)",
      "Request a copy of your information",
    ],
    outro: "Requests can be sent to the contact details provided below.",
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    body:
      "PuneSpaGuide is intended for users who are 18 years of age or older. We do not knowingly collect personal information from children under 18 years of age.",
  },
  {
    id: "external-links",
    title: "External Links",
    body:
      "Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those external websites. Users should review the privacy policies of any third-party websites they visit.",
  },
  {
    id: "changes-to-policy",
    title: "Changes to this Privacy Policy",
    body:
      'We may update this Privacy Policy from time to time. The updated version will be published on this page with a revised "Last Updated" date. Continued use of the website after changes constitutes acceptance of the revised Privacy Policy.',
  },
  {
    id: "contact-us",
    title: "Contact Us",
    contact: true,
  },
  {
    id: "compliance-indian-laws",
    title: "Compliance with Indian Laws",
    body:
      "This Privacy Policy is governed by the laws of India, including applicable provisions of the Digital Personal Data Protection Act, 2023 (DPDP Act) and other relevant laws and regulations.",
  },
  {
    id: "consent",
    title: "Consent",
    body:
      "By accessing or using PuneSpaGuide.com, creating an account, submitting information, or using any of our services, you acknowledge that you have read, understood, and agreed to this Privacy Policy.",
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy — Pune Spa Guide";
    // Feed key meta tags so social previews look right and Google sees a
    // description. Cleaned up on unmount so we don't leak across routes.
    const desc =
      "How Pune Spa Guide collects, uses, protects, and shares your personal information. Effective " +
      PRIVACY_EFFECTIVE_DATE + ". Compliant with India's Digital Personal Data Protection Act, 2023.";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
    })();
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", desc);
    return () => { if (prev !== null) meta.setAttribute("content", prev); };
  }, []);

  return (
    <div data-testid="privacy-policy-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Legal</div>
      <h1 className="font-heading text-4xl sm:text-5xl mt-2 text-[#111827]">Privacy Policy</h1>
      <p className="mt-3 text-sm text-[#4B5563]">
        Version <span className="font-medium">{PRIVACY_VERSION}</span> · Effective{" "}
        <span className="font-medium">{PRIVACY_EFFECTIVE_DATE}</span> · Last updated{" "}
        <span className="font-medium">{PRIVACY_LAST_UPDATED}</span>.
      </p>

      <div className="mt-8 rounded-xl border border-[#C9A86A] bg-[#F5EFE6] p-4 flex items-start gap-3" data-testid="privacy-summary-callout">
        <ShieldCheck size={18} className="text-[#7A5A22] shrink-0 mt-0.5" aria-hidden />
        <p className="text-sm leading-relaxed text-[#4B5563]">
          Welcome to PuneSpaGuide.com ({'"'}PuneSpaGuide{'", "'}we{'", "'}our{'", or "us"'}).
          We value your privacy and are committed to protecting the personal information you share with us.
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
          By accessing or using PuneSpaGuide.com, you agree to the practices described in this Privacy Policy.
        </p>
      </div>

      <ol className="mt-10 space-y-10" data-testid="privacy-sections">
        {PRIVACY_SECTIONS.map((s, i) => (
          <li key={s.id} id={s.id} className="scroll-mt-24">
            <h2 className="font-heading text-xl text-[#111827]">
              <span className="text-[#C9A86A] mr-2">{i + 1}.</span>{s.title}
            </h2>

            {s.body && <p className="mt-3 text-sm leading-relaxed text-[#1F2937]">{s.body}</p>}
            {s.intro && <p className="mt-3 text-sm leading-relaxed text-[#1F2937]">{s.intro}</p>}

            {s.bullets && (
              <ul className="mt-3 space-y-1.5 text-sm leading-relaxed text-[#1F2937] list-disc pl-5">
                {s.bullets.map((b) => <li key={b.slice(0, 60)}>{b}</li>)}
              </ul>
            )}

            {s.groups && (
              <div className="mt-4 space-y-5">
                {s.groups.map((g) => (
                  <div key={g.heading}>
                    <h3 className="text-sm font-medium text-[#111827]">{g.heading}</h3>
                    <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-[#1F2937] list-disc pl-5">
                      {g.bullets.map((b) => <li key={b.slice(0, 60)}>{b}</li>)}
                    </ul>
                    {g.outro && (
                      <p className="mt-2 text-sm leading-relaxed text-[#4B5563] italic">{g.outro}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {s.outro && <p className="mt-3 text-sm leading-relaxed text-[#1F2937]">{s.outro}</p>}

            {s.contact && (
              <div className="mt-3 rounded-xl bg-[#F5EFE6] border border-[#E7DDCF] p-4 text-sm text-[#1F2937] space-y-2" data-testid="privacy-contact-block">
                <p className="text-[#4B5563]">
                  For any questions regarding this Privacy Policy or your personal information, please contact us:
                </p>
                <div className="flex items-center gap-2">
                  <Mail size={14} className="text-[#7A5A22]" aria-hidden />
                  <a
                    href="mailto:support@punespaguide.com"
                    className="text-[#1F4E3D] font-medium hover:underline"
                    data-testid="privacy-contact-email"
                  >
                    support@punespaguide.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Globe size={14} className="text-[#7A5A22]" aria-hidden />
                  <a
                    href="https://punespaguide.com"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#1F4E3D] font-medium hover:underline"
                  >
                    https://punespaguide.com
                  </a>
                </div>
              </div>
            )}
          </li>
        ))}
      </ol>

      <div className="mt-12 rounded-2xl bg-[#F5EFE6] border border-[#E7DDCF] p-6 text-sm text-[#1F2937]">
        Also see our{" "}
        <Link to="/terms" className="text-[#1F4E3D] font-medium hover:underline">Owner Terms &amp; Conditions</Link>{" "}
        and{" "}
        <Link to="/contact" className="text-[#1F4E3D] font-medium hover:underline">Contact</Link> page.
      </div>
    </div>
  );
}
